import React from 'react';
import { Badge, tokens } from '@fluentui/react-components';

// Booqable's structural `stopped` status is labelled "Completed" for staff —
// the system term stays `stopped`, this is display-only. Other labels match
// the lifecycle terms.
type StatusPill = {
  label: string;
  color: 'brand' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle' | 'success' | 'warning';
  appearance: 'filled' | 'outline' | 'ghost' | 'tint';
};

const STATUS_PILLS: Record<string, StatusPill> = {
  draft:    { label: 'Draft',    color: 'subtle',      appearance: 'filled' },
  reserved: { label: 'Reserved', color: 'informative', appearance: 'filled' },
  started:  { label: 'Started',  color: 'warning',     appearance: 'filled' },
  stopped:  { label: 'Completed', color: 'success',     appearance: 'filled' },
  archived: { label: 'Archived', color: 'subtle',      appearance: 'filled' },
  canceled: { label: 'Canceled', color: 'subtle',      appearance: 'outline' },
};

// Fixed-width pills with extra padding so labels breathe and the orders
// table reads as a uniform stack down the column.
const PILL_STYLE: React.CSSProperties = {
  paddingTop: '4px',
  paddingBottom: '4px',
  paddingLeft: '12px',
  paddingRight: '12px',
  height: 'auto',
  width: '96px',
  justifyContent: 'center',
  textAlign: 'center',
};

interface OrderStatusPillProps {
  status: string | null | undefined;
  /** Optional leading icon rendered inside the pill (e.g. a dimension glyph
   *  in the order header). Omitted in table contexts. */
  icon?: React.ReactNode;
  /** Render a quiet translucent-white "ghost" pill for placement on a
   *  brand-colored surface (e.g. the project phase card header), where the
   *  per-status filled colors clash. The label still conveys the status. */
  onBrand?: boolean;
}

const OrderStatusPill: React.FC<OrderStatusPillProps> = ({ status, icon, onBrand }) => {
  if (!status) return <>—</>;
  const key = status.toLowerCase();
  const pill = STATUS_PILLS[key];
  // Ghost pill on a brand-colored header — uniform white-on-blue, label-only.
  if (onBrand) {
    return (
      <Badge
        appearance="ghost"
        style={{
          ...PILL_STYLE,
          width: 'auto',
          backgroundColor: 'rgba(255, 255, 255, 0.18)',
          color: tokens.colorNeutralForegroundOnBrand,
          border: '1px solid rgba(255, 255, 255, 0.45)',
        }}
      >
        {pill?.label ?? status}
      </Badge>
    );
  }
  if (!pill) {
    return <Badge appearance="filled" color="subtle" icon={icon} style={PILL_STYLE}>{status}</Badge>;
  }
  // Force white-on-blue for Reserved — Fluent's `informative` filled
  // variant renders as light-blue tint in some themes; explicit override
  // guarantees the staff-requested high-contrast blue pill.
  if (key === 'reserved') {
    return (
      <Badge
        icon={icon}
        appearance="filled"
        style={{
          ...PILL_STYLE,
          backgroundColor: tokens.colorBrandBackground,
          color: tokens.colorNeutralForegroundOnBrand,
        }}
      >
        {pill.label}
      </Badge>
    );
  }
  // Grey fill / black text for Draft.
  if (key === 'draft') {
    return (
      <Badge
        icon={icon}
        appearance="filled"
        style={{
          ...PILL_STYLE,
          backgroundColor: tokens.colorNeutralBackground3,
          color: tokens.colorNeutralForeground1,
        }}
      >
        {pill.label}
      </Badge>
    );
  }
  // Amber fill / dark text for Started — dark text keeps contrast legible
  // on the light amber.
  if (key === 'started') {
    return (
      <Badge
        icon={icon}
        appearance="filled"
        style={{
          ...PILL_STYLE,
          backgroundColor: 'rgb(255, 180, 66)',
          color: tokens.colorNeutralForeground1,
        }}
      >
        {pill.label}
      </Badge>
    );
  }
  // Green fill / dark text for Completed — dark text keeps contrast legible
  // on the light green.
  if (key === 'stopped') {
    return (
      <Badge
        icon={icon}
        appearance="filled"
        style={{
          ...PILL_STYLE,
          backgroundColor: 'rgb(98, 218, 124)',
          color: tokens.colorNeutralForeground1,
        }}
      >
        {pill.label}
      </Badge>
    );
  }
  // White fill / grey border / dark text for terminal states.
  if (key === 'canceled' || key === 'archived') {
    return (
      <Badge
        icon={icon}
        appearance="outline"
        style={{
          ...PILL_STYLE,
          backgroundColor: tokens.colorNeutralBackground1,
          color: tokens.colorNeutralForeground1,
          borderColor: tokens.colorNeutralStroke1,
        }}
      >
        {pill.label}
      </Badge>
    );
  }
  return (
    <Badge appearance={pill.appearance} color={pill.color} icon={icon} style={PILL_STYLE}>
      {pill.label}
    </Badge>
  );
};

export default OrderStatusPill;
